import { cn } from "@/lib/utils";
import { can as hasPermission, useSession } from "@/shared/auth/session";
import { NavLink, Outlet, useLocation } from "react-router-dom";

/**
 * Know Your Polling Station Mate — layout with tab groups.
 *
 * Pattern: ProgramAnalyticsLayout / PollingLayout. AppShell takes only
 * { children } and its h-9 sub-bar has no slot — this layout does not touch
 * the shell.
 *
 * A group whose every tab is denied is hidden. Individual tabs are NOT hidden
 * on a maybe: the server's refusal is what speaks (§16.7, rule 2.4).
 */

interface Tab {
  to: string;
  label: string;
  hint: string;
  permission?: string;
  end?: boolean;
}

interface Group {
  label: string;
  tabs: Tab[];
}

export const STATION_MATES_GROUPS: Group[] = [
  {
    label: "Polling day",
    tabs: [
      {
        to: "/station-mates",
        label: "The Floor",
        hint: "Every cell against the closing clock — who is still out, and is there time",
        end: true,
        permission: "turnout:analyse",
      },
    ],
  },
  {
    label: "Overview",
    tabs: [
      {
        to: "/station-mates/overview",
        label: "Overview",
        hint: "Election KPIs, coverage and the day's state",
      },
      // { to: '/station-mates/elections', label: 'Elections', hint: 'Create, phase transitions, deadlines, close' },
      {
        to: "/station-mates/stations",
        label: "Stations",
        hint: "Grid ranked by outstanding count",
      },
      {
        to: "/station-mates/cells",
        label: "Cells",
        hint: "List with drawer: members, coordinator, pledges, awards, flags",
      },
    ],
  },
  {
    label: "Field",
    tabs: [
      // {
      //   to: "/station-mates/builder",
      //   label: "Cell builder",
      //   hint: "Proposal → map → drag-to-reassign → apply",
      //   permission: "turnout:manage-cells",
      // },
      {
        to: "/station-mates/pledges",
        label: "Pledges",
        hint: "Capture wizard, intent-decay list, transport-gap list",
      },
      {
        to: "/station-mates/board",
        label: "My cell",
        hint: "The coordinator board — who do I still need to get?",
        permission: "turnout:field",
      },
    ],
  },
  {
    label: "Polling day",
    tabs: [
      {
        to: "/station-mates/live",
        label: "Live ops",
        hint: "Map by outstanding count, queue feed, escalation queue with ageing",
      },
      {
        to: "/station-mates/pace",
        label: "Who is behind",
        hint: "The 11:00 and 13:00 readings — slow stations, their cells, and who to call",
        permission: "turnout:analyse",
      },
      {
        to: "/station-mates/transport",
        label: "Transport",
        hint: "Offers, requests, unmatched-first",
      },
    ],
  },
  {
    label: "Manage",
    tabs: [
      {
        to: "/station-mates/incentives",
        label: "Incentives",
        hint: "Budgets, awards, spend, by cell",
        permission: "turnout:incentive-read",
      },
      {
        to: "/station-mates/integrity",
        label: "Integrity",
        hint: "Flag queue, review, resolution",
        permission: "turnout:moderate",
      },
      {
        to: "/station-mates/analytics",
        label: "Analytics",
        hint: "Funnel, velocity, cost per member, lift",
        permission: "turnout:analyse",
      },
    ],
  },
];

export function StationMatesLayout() {
  const { search } = useLocation();
  const authorities = useSession((s) => s.authorities);

  const groups = STATION_MATES_GROUPS.map((group) => ({
    ...group,
    tabs: group.tabs.filter((tab) =>
      hasPermission(authorities, tab.permission),
    ),
  })).filter((group) => group.tabs.length > 0);

  return (
    <div className="space-y-4">
      <nav
        aria-label="Know Your Polling Station Mate"
        className="axis-table-scroll border-b border-border"
      >
        <ul className="flex items-stretch gap-4 min-w-max">
          {groups.map((group) => (
            <li key={group.label} className="flex items-stretch gap-1">
              <span
                className="self-center pr-1 text-erp-xs uppercase tracking-wide text-muted-foreground/70"
                aria-hidden
              >
                {group.label}
              </span>
              {group.tabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={{ pathname: tab.to, search }}
                  end={tab.end}
                  title={tab.hint}
                  className={({ isActive }) =>
                    cn(
                      "inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium border-b-2 -mb-px transition-colors",
                      isActive
                        ? "border-chocolate text-chocolate"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    )
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </li>
          ))}
        </ul>
      </nav>
      <Outlet />
    </div>
  );
}
